<template>
  <div class="w-full" aria-label="US Checkouts Map">
    <div class="panel-neo panel-neo--glassier rounded-xl px-4 pt-5 pb-4">
      <div class="flex items-center justify-end mb-3">
        <div class="flex items-center gap-3 text-[10px] text-slate-400 flex-wrap justify-end">
          <div class="inline-flex items-center gap-2 rounded border border-slate-700/70 bg-slate-900/60 px-2 py-1">
            <span class="w-2.5 h-2.5 rounded-full bg-sky-300"></span><span>Checked Out</span>
            <span class="w-2.5 h-2.5 rounded-full bg-sky-500 map-legend-pulse"></span><span>In Transit</span>
            <span class="w-2.5 h-2.5 rounded-full bg-emerald-400"></span><span>Delivered</span>
            <span class="w-2.5 h-2.5 rounded-full bg-yellow-400"></span><span>Delivery Overdue</span>
          </div>
          <button
            @click="showHelp = true"
            title="How to use map"
            class="inline-flex items-center gap-1 hover:text-white transition-colors"
          >
            <svg
              class="w-3.5 h-3.5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M12 17v-4a2 2 0 1 1 2-2" />
              <path d="M12 8h.01" />
            </svg>
            <span>Tips</span>
          </button>
          <a
            href="/docs/mapping-system.md"
            target="_blank"
            rel="noopener"
            title="Mapping System Docs"
            class="inline-flex items-center gap-1 hover:text-white transition-colors"
          >
            <svg
              class="w-3.5 h-3.5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M12 16v-4" />
              <path d="M12 8h.01" />
            </svg>
            <span>Guide</span>
          </a>
          <div>48 contiguous states</div>
        </div>
      </div>
      <div
        class="relative h-[565px] max-h-[590px] w-full overflow-hidden rounded-lg select-none"
        @wheel.prevent="onWheel"
      >
        <svg
          ref="mapSvg"
          class="absolute inset-0 w-full h-full z-0"
          viewBox="0 0 959 593"
          role="img"
          aria-labelledby="title desc"
          preserveAspectRatio="xMidYMid meet"
          @dblclick.stop="zoomAt($event, 1.5)"
          @click.alt.exact.stop="zoomAt($event, 0.75)"
        >
          <title id="title">US Map (Contiguous)</title>
          <desc id="desc">Per-state inline SVG for interactive styling</desc>
          <defs>
            <pattern id="grid" width="24" height="24" patternUnits="userSpaceOnUse">
              <path
                d="M 24 0 L 0 0 0 24"
                fill="none"
                stroke="rgba(255,255,255,0.04)"
                stroke-width="1"
              />
            </pattern>
          </defs>
          <rect x="0" y="0" width="100%" height="100%" fill="url(#grid)" />

          <!-- Accurate per-state paths generated from us-atlas topojson -->
          <g stroke="#374151" stroke-width="0.6" :transform="zoomTransform">
            <template v-for="st in states" :key="st.id">
              <path
                :id="st.id"
                :d="st.d"
                :fill="fillFor(st.id)"
                style="transition: fill 180ms ease"
                @mouseenter="onEnter(st.name, $event)"
                @mousemove="onMove($event)"
                @mouseleave="onLeave"
              />
            </template>
          </g>
          <defs>
            <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
              <feDropShadow
                dx="0"
                dy="1"
                stdDeviation="1.2"
                flood-color="#000"
                flood-opacity="0.35"
              />
            </filter>
          </defs>
        </svg>

        <!-- Bubble markers for checked-out items -->
        <!-- Markers overlay: match the same coordinate space (viewBox) as the base map -->
        <svg
          class="absolute inset-0 w-full h-full pointer-events-none z-20"
          viewBox="0 0 959 593"
          preserveAspectRatio="xMidYMid meet"
          @dblclick.stop="zoomAt($event, 1.5)"
          @click.alt.exact.stop="zoomAt($event, 0.75)"
        >
          <g :transform="zoomTransform">
            <g
              v-for="p in points"
              :key="p.id"
              class="pointer-events-auto"
              @dblclick.stop="zoomAt($event, 1.5)"
              @click.alt.exact.stop="zoomAt($event, 0.75)"
            >
              <circle
                :cx="p.x"
                :cy="p.y"
                r="6"
                :class="['map-marker', markerClass(p)]"
                stroke="rgba(255,255,255,0.24)"
                stroke-width="1.4"
                filter="url(#shadow)"
                @mouseenter.stop="
                  onEnter(
                    `${p.label} • ${p.organization || '—'} • ${[p.city, p.state].filter(Boolean).join(', ')}`,
                    $event,
                  )
                "
                @mousemove.stop="onMove($event)"
                @mouseleave.stop="onLeave"
              />
              <circle :cx="p.x" :cy="p.y" r="2.2" fill="#fff" fill-opacity="0.85" />
            </g>
          </g>
        </svg>

        <!-- Zoom controls -->
        <div class="absolute right-2 bottom-2 z-30 flex flex-col gap-1">
          <button
            @click="zoomBy(1.5)"
            class="w-8 h-8 rounded-md bg-black/40 text-white hover:bg-black/60 transition-colors flex items-center justify-center"
            title="Zoom in"
          >
            +
          </button>
          <button
            @click="zoomBy(0.75)"
            class="w-8 h-8 rounded-md bg-black/40 text-white hover:bg-black/60 transition-colors flex items-center justify-center"
            title="Zoom out"
          >
            −
          </button>
          <button
            @click="resetZoom"
            class="w-8 h-8 rounded-md bg-black/40 text-white hover:bg-black/60 transition-colors flex items-center justify-center"
            title="Reset"
          >
            ↺
          </button>
        </div>

        <!-- Hover tooltip -->
        <div
          v-if="hoverState"
          class="absolute pointer-events-none px-2 py-1 rounded bg-black/70 text-[10px] text-white"
          :style="{ left: tooltipX + 'px', top: tooltipY + 'px', transform: 'translateY(-8px)' }"
        >
          {{ hoverState }}
        </div>

        <!-- Help modal -->
        <transition name="modal-scale" appear>
          <div v-if="showHelp" class="absolute inset-0 z-40 flex items-center justify-center">
            <div class="absolute inset-0 bg-black/50" @click="showHelp = false"></div>
            <div class="relative w-[520px] max-w-[92vw] rounded-xl glass p-5 text-slate-200">
              <h4 class="text-white text-base font-semibold mb-2">Map Tips</h4>
              <ul class="list-disc pl-5 space-y-1 text-sm">
                <li><b>Double‑click</b> to zoom in at the cursor</li>
                <li><b>Alt+click</b> to zoom out at the cursor</li>
                <li><b>Mouse wheel</b> zooms in/out (cursor‑centric)</li>
                <li>Use <b>+</b>, <b>−</b>, and <b>↺</b> buttons (bottom‑right)</li>
                <li>Hover a point to see <b>Equipment • Organization • City, ST</b></li>
              </ul>
              <div class="mt-4 flex justify-end gap-2">
                <button
                  class="px-3 py-1 text-xs bg-slate-900 text-white rounded-md"
                  @click="showHelp = false"
                >
                  Close
                </button>
                <a
                  href="/docs/mapping-system.md"
                  target="_blank"
                  class="px-3 py-1 text-xs btn-primary rounded-md"
                  >Full Guide</a
                >
              </div>
            </div>
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { feature } from 'topojson-client'
import usStates from 'us-atlas/states-10m.json'
import { geoPath, geoAlbersUsa } from 'd3-geo'
import {
  listCheckedOutLocations,
  type CheckoutLocation,
} from '@/lib/appApi'
import { createLogger } from '@/lib/logger'

const log = createLogger('Map')
const DATA_UPDATED_EVENT = 'testdrive:data-updated'

const hoverState = ref('')
const tooltipX = ref(0)
const tooltipY = ref(0)

// Build state shapes once from topojson (lower 48)
type StateShape = { id: string; name: string; d: string }
const states = ref<StateShape[]>([])
const activeStates = new Set<string>()

type ScreenPoint = CheckoutLocation & { organization?: string; x: number; y: number }
const points = ref<ScreenPoint[]>([])
const showHelp = ref(false)
let autoRefreshTimer: ReturnType<typeof setInterval> | null = null

// Zoom / Pan state
const scale = ref(1)
const tx = ref(0)
const ty = ref(0)
const zoomTransform = computed(() => `translate(${tx.value},${ty.value}) scale(${scale.value})`)

function onEnter(label: string, ev?: MouseEvent) {
  hoverState.value = label
  if (ev) {
    tooltipX.value = ev.offsetX + 10
    tooltipY.value = ev.offsetY - 10
  }
}
function onMove(ev: MouseEvent) {
  tooltipX.value = ev.offsetX + 10
  tooltipY.value = ev.offsetY - 10
}
function onLeave() {
  hoverState.value = ''
}

function fillFor(code: string): string {
  // Base: dark slate; Active: teal
  return activeStates.has(code) ? 'var(--poc)' : 'rgba(148,163,184,0.25)'
}

// Initialize shapes
const geo = feature(usStates as any, (usStates as any).objects.states) as any
const projection = geoAlbersUsa().fitSize([959, 593], geo)
const pathGen = geoPath(projection)
states.value = geo.features
  .filter((f: any) => ![2, 15, 72, 78, 60, 66, 69].includes(f.id))
  .map((f: any) => ({
    id: f.properties?.postal || String(f.id),
    name: f.properties?.name || String(f.id),
    d: pathGen(f) || '',
  }))

// Project lon/lat into screen coords using the same identity fit
async function loadPoints() {
  log.debug('Loading checkout locations...')

  try {
    const rows = await listCheckedOutLocations()
    log.debug('Got checkout locations:', rows.length, rows)

    const dataToUse = rows

    function hash(s: string) {
      let h = 0
      for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0
      return h
    }

    points.value = dataToUse.map((r, idx) => {
      const xy = projection([r.lon, r.lat]) as [number, number]
      // Deterministic micro-jitter to reveal stacked points at centroids
      const h = hash(r.id + (r.state || '') + (r.city || '') + String(idx))
      const angle = ((h % 360) * Math.PI) / 180
      const radius = 0.0 + (h % 7) // 0..6 px
      const jx = xy[0] + radius * Math.cos(angle)
      const jy = xy[1] + radius * Math.sin(angle)
      return { ...r, organization: (r as any).organization_name || 'Demo Company', x: jx, y: jy }
    })

    log.debug('Processed points for display:', points.value.length)
  } catch (error) {
    log.error('Error loading checkout locations:', error)
    points.value = []
  }
}
loadPoints()

// Zoom helpers
function clamp(v: number, min: number, max: number) {
  return Math.max(min, Math.min(max, v))
}
function zoomAt(ev: MouseEvent, factor: number) {
  const rect = (ev.currentTarget as SVGElement).getBoundingClientRect()
  const cx = ev.clientX - rect.left
  const cy = ev.clientY - rect.top
  const s = clamp(scale.value * factor, 1, 6)
  // Translate so the point under cursor stays under cursor after scaling
  tx.value = cx - (cx - tx.value) * (s / scale.value)
  ty.value = cy - (cy - ty.value) * (s / scale.value)
  scale.value = s
}
function zoomBy(factor: number) {
  // Zoom around center of viewport
  const evLike: any = {
    currentTarget: document.querySelector('svg[viewBox="0 0 959 593"]'),
    clientX: 959 / 2,
    clientY: 593 / 2,
  }
  zoomAt(evLike as MouseEvent, factor)
}
function onWheel(ev: WheelEvent) {
  const factor = ev.deltaY < 0 ? 1.15 : 0.87
  zoomAt(ev as any, factor)
}
function resetZoom() {
  scale.value = 1
  tx.value = 0
  ty.value = 0
}

function isOverdue(p: ScreenPoint) {
  const due = (p as any).expectedReturnAt
  if (!due) return false
  const dueMs = new Date(due).getTime()
  return Number.isFinite(dueMs) && Date.now() > dueMs
}

function markerClass(p: ScreenPoint) {
  const status = String((p as any).status || '').toLowerCase()
  if (isOverdue(p)) return 'map-marker--overdue'
  if (status.includes('in_transit') || status.includes('out_for_delivery') || status.includes('carrier')) {
    return 'map-marker--transit'
  }
  if (status === 'with_borrower' || status.includes('delivered')) {
    return 'map-marker--delivered'
  }
  return 'map-marker--checkedout'
}

// ESC to close help
function onKey(e: KeyboardEvent) {
  if (e.key === 'Escape') showHelp.value = false
}
function onDataUpdated() {
  void loadPoints()
}

onMounted(() => {
  window.addEventListener('keydown', onKey)
  window.addEventListener(DATA_UPDATED_EVENT, onDataUpdated)
  autoRefreshTimer = setInterval(() => {
    void loadPoints()
  }, 15000)
})
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKey)
  window.removeEventListener(DATA_UPDATED_EVENT, onDataUpdated)
  if (autoRefreshTimer) {
    clearInterval(autoRefreshTimer)
    autoRefreshTimer = null
  }
})
</script>
<style scoped>
.map-marker {
  fill-opacity: 0.9;
}
.map-marker--checkedout {
  fill: #7dd3fc;
}
.map-marker--transit {
  fill: #0ea5e9;
  animation: mapPulse 1.1s ease-in-out infinite;
}
.map-marker--delivered {
  fill: #22c55e;
}
.map-marker--overdue {
  fill: #facc15;
}
.map-legend-pulse {
  animation: mapPulse 1.1s ease-in-out infinite;
}
@keyframes mapPulse {
  0% { opacity: 1; }
  50% { opacity: 0.35; }
  100% { opacity: 1; }
}
</style>
